import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { fetchSiteSettings } from '../api/siteSettings';
import { normalizeSiteConfig, secureAssetUrl } from '../utils/secureAssetUrl';

const SiteConfigContext = createContext(null);

const DEFAULT_CONFIG = {
  site_name: 'Polyrion',
  logo_text: 'POLYRION',
  logo_type: 'icon_text',
  favicon_url: null,
  logo_light_url: null,
  logo_dark_url: null,
  logo_icon_url: null,
  meta_description: null,
  theme_color: null,
  hero: {
    bg_start: '#10131d',
    bg_end: '#171a24',
    bg_image_url: null,
    title: 'Polyrion Blockchain',
    title_accent: 'Analytics',
    search_placeholder: 'Search transactions, blocks, programs and tokens',
    note: 'Solscan - Get your ad in the spotlight!',
    note_link_text: 'Book your slot today!',
    note_link_url: '/',
  },
  sponsored: { image_url: null, link_url: null },
  maintenance: { enabled: false, message: null },
};

function applyDocumentMeta(config) {
  if (!config) {
    return;
  }

  document.title = config.site_name || DEFAULT_CONFIG.site_name;

  const iconHref = secureAssetUrl(config.favicon_url || config.logo_icon_url);
  if (iconHref) {
    ['icon', 'shortcut icon'].forEach((rel) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement('link');
        el.rel = rel;
        document.head.appendChild(el);
      }
      el.href = iconHref;
    });
  }

  if (config.meta_description) {
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'description';
      document.head.appendChild(meta);
    }
    meta.content = config.meta_description;
  }

  if (config.theme_color) {
    let themeMeta = document.querySelector('meta[name="theme-color"]');
    if (!themeMeta) {
      themeMeta = document.createElement('meta');
      themeMeta.name = 'theme-color';
      document.head.appendChild(themeMeta);
    }
    themeMeta.content = config.theme_color;
  }

  const heroStart = config.hero?.bg_start || DEFAULT_CONFIG.hero.bg_start;
  const heroEnd = config.hero?.bg_end || DEFAULT_CONFIG.hero.bg_end;
  document.documentElement.style.setProperty('--hero-bg-start', heroStart);
  document.documentElement.style.setProperty('--hero-bg-end', heroEnd);
}

export function SiteConfigProvider({ children }) {
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchSiteSettings();
      const normalized = normalizeSiteConfig({ ...DEFAULT_CONFIG, ...data, maintenance: data.maintenance ?? DEFAULT_CONFIG.maintenance });
      setConfig(normalized);
      applyDocumentMeta(normalized);
    } catch (err) {
      setError(err.message || 'Failed to load site settings');
      applyDocumentMeta(DEFAULT_CONFIG);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  useEffect(() => {
    const onMaintenance = () => {
      reload();
    };
    window.addEventListener('polyrion:maintenance', onMaintenance);
    return () => window.removeEventListener('polyrion:maintenance', onMaintenance);
  }, [reload]);

  const value = useMemo(
    () => ({
      config,
      loading,
      error,
      reload,
      isMaintenance: Boolean(config.maintenance?.enabled),
    }),
    [config, loading, error, reload],
  );

  return <SiteConfigContext.Provider value={value}>{children}</SiteConfigContext.Provider>;
}

export function useSiteConfig() {
  const ctx = useContext(SiteConfigContext);
  if (!ctx) {
    throw new Error('useSiteConfig must be used within SiteConfigProvider');
  }
  return ctx;
}
